import React, {
  useMemo,
} from 'react';
import {
  VisitInfo,
} from 'pages/Dashboard/pages/Billing/types';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import isNil from 'lodash/isNil';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  isEmptyString,
} from '@populateui/utils';
import EOBCodesPopover, {
  EOBCodesComponentProps,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBCodesPopover';
import {
  ClaimPaymentRemarkCodeDTO,
  EOBClaimPaymentViewDTO,
} from 'dtos';
import RemittanceAdviceRemarkCodeLookup from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/RemittanceAdviceRemarkCodeLookup';
import head from 'lodash/head';

export type ClaimPaymentRemarkCodeParams = {
  claimId: number;
  eobClaimPayments: EOBClaimPaymentViewDTO[];
};

type Props = {
  data: VisitInfo;
  readonly?: boolean;
};

const formKey = 'claimPaymentRemarkCodes';

function ClaimPaymentRemarkCodes({ index }: EOBCodesComponentProps) {
  const { control } = useFormContext();

  return (
    <div className="w-full">
      <Controller
        name={`${formKey}.${index}.remarkCode`}
        control={control}
        rules={{
          required: 'Claim Payment Remark Code is required',
          validate: (value) => !isEmptyString(value?.trim()),
        }}
        render={({ field, fieldState: { error } }) => (
          <RemittanceAdviceRemarkCodeLookup
            {...field}
            error={!isNil(error)}
            id="claim-payment-remark-code"
            label={index === 0 ? 'Claim Payment Remark Code' : undefined}
            placeholder="Claim Payment Remark Code"
          />
        )}
      />
    </div>
  );
}

function getActiveClaimPayment(
  eobClaimPayments: EOBClaimPaymentViewDTO[],
  eobNumber?: string,
): EOBClaimPaymentViewDTO | undefined {
  if (isEmptyString(eobNumber)) {
    return head(eobClaimPayments);
  }

  return eobClaimPayments.find((payment) => payment?.eobNumber === eobNumber)
    ?? head(eobClaimPayments);
}

export default function ClaimPaymentRemarkCodesPopover({
  data,
  readonly,
}: Props) {
  const { watch } = useFormContext();
  const eobNumber = watch('eob.eobNumber') as string | undefined;
  const claimId = data?.claim?.claimId ?? data?.claimId ?? 0;
  const eobClaimPayments = data?.eobClaimPayments ?? [];
  const remarkCodes = eobClaimPayments.flatMap((payment) => payment?.remarkCodes ?? []);

  const activeClaimPayment = useMemo(
    () => getActiveClaimPayment(eobClaimPayments, eobNumber),
    [eobClaimPayments, eobNumber],
  );

  return (
    <EOBCodesPopover<ClaimPaymentRemarkCodeDTO>
      data={remarkCodes}
      readonly={readonly}
      formKey={formKey}
      CodePopoverComponent={ClaimPaymentRemarkCodes}
      getLabel={(item) => item?.remarkCode ?? ''}
      tooltip="Add Claim Payment Remark Code"
      onSubmit={(claimPaymentRemarkCodes) => {
        const activePayment = activeClaimPayment ?? {
          claimId,
          eobNumber: eobNumber ?? '',
        };

        const updatedClaimPayment: EOBClaimPaymentViewDTO = {
          ...activePayment,
          claimId,
          claimPatientId: activePayment?.claimPatientId
            ?? data?.patientId
            ?? data?.claim?.patientId
            ?? 0,
          eobNumber: activePayment?.eobNumber ?? eobNumber ?? '',
          remarkCodes: claimPaymentRemarkCodes,
        };

        const replaced = eobClaimPayments.some((payment) => (
          payment?.claimId === updatedClaimPayment?.claimId
          && payment?.eobNumber === updatedClaimPayment?.eobNumber
        ));
        const nextClaimPayments = replaced
          ? eobClaimPayments.map((payment) => (
            payment?.claimId === updatedClaimPayment?.claimId
            && payment?.eobNumber === updatedClaimPayment?.eobNumber
              ? updatedClaimPayment
              : payment
          ))
          : [...eobClaimPayments, updatedClaimPayment];

        paymentsEventChannel.emit('onChangeEOBClaimPaymentRemarkCodes', {
          claimId,
          eobClaimPayments: nextClaimPayments,
        });
      }}
      popoverProps={{
        classes: {
          paper: '!w-[300px]',
        },
        onClick: (event) => {
          event.stopPropagation();
        },
        onMouseDown: (event) => {
          event.stopPropagation();
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
      }}
    />
  );
}
